import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * interfaces
 */
import { ILoadingControlService, ILoadingHttpControlService, ILoadingStatusService } from './interfaces';

/**
 * Loading Service
 * 
 * @description Manages global loading state for HTTP requests.
 * Tracks active requests and provides loading state to components.
 * Automatically shows/hides loading indicators based on request activity.
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
@Injectable()
export class LoadingService implements ILoadingControlService, ILoadingHttpControlService, ILoadingStatusService {

    /**
     * Set of active requests
     * @type {Set<string>}
     * @description The set contains unique identifiers for active requests.
     * It is used to track which requests are currently active.
     * 
     * @example
     * ```typescript
     * this.activeRequests.add('request1');
     * this.activeRequests.add('request2');
     * this.activeRequests.delete('request1');
     * console.log(this.activeRequests.size); // 1
     * ```
    */
    private _activeRequests = new Set<string>();

    /**
     * Subject that emits the current loading state
     * @type {BehaviorSubject<boolean>}
     * 
     * @description The subject emits a boolean value indicating whether there are active requests.
     * It is used to notify components when the loading state changes.
     * 
     * @example
     * ```typescript
     * this.loading$.subscribe((isLoading) => {
     *   console.log(isLoading);
     * });
     */
    private _loadingSubject = new BehaviorSubject<boolean>(false);

    /**
     * Observable that emits the current loading state
     * @returns {Observable<boolean>} True when requests are active, false when all requests complete
     */
    public get loading$(): Observable<boolean> {
        return this._loadingSubject.asObservable();
    }

    /**
     * Getter for current loading state
     * @returns {boolean} Current loading state
     */
    public get isLoading(): boolean {
        return this._loadingSubject.value;
    }

    /**
     * Getter for active requests count
     * @returns {number} Number of active requests
     */
    public get activeRequestsCount(): number {
        return this._activeRequests.size;
    }

    /**
     * Adds a request to the active requests set
     * @param {string} requestId - Unique identifier for the request
     */
    public addRequest(requestId: string): void {

        // Add request to active requests
        this._activeRequests.add(requestId);

        // Update loading state
        this._updateLoadingState();

    }

    /**
     * Removes a request from the active requests set
     * @param {string} requestId - Unique identifier for the request
     */
    public removeRequest(requestId: string): void {

        // Remove request from active requests
        this._activeRequests.delete(requestId);

        // Update loading state
        this._updateLoadingState();
    }

    /**
     * Clears all active requests (useful for error scenarios)
     */
    public clearAllRequests(): void {

        // Clear all active requests
        this._activeRequests.clear();

        // Update loading state
        this._updateLoadingState();
    }

    /**
     * Updates the loading state based on active requests
     * @private
     */
    private _updateLoadingState(): void {

        // Get loading state
        const isLoading = this._activeRequests.size > 0;
        
        // If loading state is the same, return
        if (this.isLoading === isLoading) return;

        // Update loading state
        this._loadingSubject.next(isLoading);
    }

    /**
     * Generates a unique request ID based on URL, method, and active requests count
     * @param {string} url - Request URL
     * @param {string} method - HTTP method
     * @returns {string} Unique request identifier
     */
    public generateRequestId(url: string, method: string): string {
        
        // Generate request ID based on URL, method, and active requests count
        const activeCount = this._activeRequests.size;

        // Generate timestamp
        const timestamp = Date.now();

        // Generate UUID
        const uuid = `${method.toUpperCase()}_${url}_${activeCount}_${timestamp}`;

        return uuid.toUpperCase();
        
    }

    // ===== Manual Loading Control Methods =====

    /**
     * Shows the loading indicator manually
     * @description Manually activates the loading state (useful for non-HTTP scenarios)
     */
    public show(): void {
        this._loadingSubject.next(true);
    }

    /**
     * Hides the loading indicator manually
     * @description Manually deactivates the loading state
     */
    public hide(): void {
        this._loadingSubject.next(false);
    }

    /**
     * Toggles the loading state
     * @description Switches between loading and not loading states
     */
    public toggle(): void {
        this._loadingSubject.next(!this.isLoading);
    }

    /**
     * Sets the loading state to a specific value
     * @param {boolean} isLoading - The loading state to set
     * @description Manually sets the loading state to true or false
     */
    public setLoading(isLoading: boolean): void {
        this._loadingSubject.next(isLoading);
    }
}
